import { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import { StripePaymentElementOptions } from '@stripe/stripe-js'

import { PageName } from '@/enums/pageName'
import { PaymentMethodService } from '@/enums/paymentMethodServices'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { PaymentScreenVersion } from '@/enums/paymentScreenVersion'
import { getPath } from '@/utils/urlUtils'

import { PrimaryButtonWithIcon } from '@/components/buttons/primaryButton'

import { ApiService } from '@/api'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { useSession } from '@/core/session/useSession'
import { trackPaymentInfoAdded, trackPaymentClick, trackPurchaseStarted } from '@/events/events'

import { TStripeCheckoutFormProps } from './types'
import { PaymentMethodIcons } from '../paymentMethodIcons'

const options: StripePaymentElementOptions = {
  fields: { billingDetails: { address: { country: 'never' } } },
  terms: { card: 'never' },
  wallets: {
    applePay: 'never',
    googlePay: 'never',
  },
}

const getResultUrl = (paymentResultUrl: string, successPage: PageName): URL => {
  const fail = PageName.paymentFailed
  const url = new URL(paymentResultUrl)
  url.searchParams.append('success_page', successPage)
  url.searchParams.append('fail_page', fail)
  url.searchParams.append('payment_method', PaymentMethod.CARD)

  return url
}

export const StripeCheckoutForm = ({
  pageName,
  planDetails,
  setItem,
  successRedirectTo,
  config,
  formConfig,
}: TStripeCheckoutFormProps) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const sessionContext = useSession()

  const stripe = useStripe()
  const elements = useElements()

  const [isLoading, setIsLoading] = useState<boolean>(true) // will be false when stripe is ready

  const paymentMethodsOrder = formConfig?.paymentMethodsSection?.order || [
    PaymentMethodService.VISA_BW,
    PaymentMethodService.MASTERCARD_BW,
    PaymentMethodService.MAESTRO_BW,
    PaymentMethodService.DISCOVER_BW,
  ]

  const showPaymentMethodsIcons = PaymentScreenVersion.default === config.PAYMENT_SCREEN_VERSION

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!stripe || !elements || !user) {
      // Stripe.js hasn't yet loaded.
      return
    }

    setIsLoading(true)

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setIsLoading(false)
      return
    }
    setItem('paymentMethod', PaymentMethod.CARD)
    setItem('paymentPlatform', PaymentPlatform.STRIPE)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promisesList: Promise<any>[] = [
      trackPurchaseStarted(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planDetails.internalPlanId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        user
      ),
    ]

    setItem(PageName.paymentScreen, '')

    // TODO: is stage variation ok?
    promisesList.push(
      trackPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planDetails.internalPlanId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user,
        [],
        ['card']
      )
    )
    promisesList.push(
      trackPaymentClick(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planDetails.internalPlanId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user
      )
    )

    const redirectToResultPage = async (
      paymentResultUrl: string,
      type: string,
      clientSecret: string
    ) => {
      const redirectTo = getResultUrl(paymentResultUrl, successRedirectTo)
      if (type === 'setup') {
        const intent = await stripe.retrieveSetupIntent(clientSecret)
        if (intent.error) {
          navigate(getPath(PageName.paymentFailed))
          return
        }
        redirectTo.searchParams.append('setup_intent', intent.setupIntent.id)
      } else {
        const intent = await stripe.retrievePaymentIntent(clientSecret)
        if (intent.error) {
          navigate(getPath(PageName.paymentFailed))
          return
        }
        redirectTo.searchParams.append('payment_intent', intent.paymentIntent.id)
      }
      Promise.all(promisesList).finally(() => {
        window.location.href = redirectTo.href
      })
    }

    resetContactStateCheckTimeout(setItem)

    const payload = {
      page_set_id: config.PAGE_SET_ID,
      plan_id: planDetails.internalPlanId,
      wallet_name: 'card',
    }

    let response
    try {
      response = await ApiService.subscribePlan(payload)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 409) {
        navigate(getPath(successRedirectTo))
        return
      }
      console.log('error1', error)
      navigate(getPath(PageName.paymentFailed))
      return
    }

    const {
      type,
      client_secret: clientSecret,
      country,
      payment_result_url: paymentResultUrl,
    } = response.data
    const confirmSubscription = type === 'setup' ? stripe.confirmSetup : stripe.confirmPayment

    try {
      const { error } = await confirmSubscription({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getResultUrl(paymentResultUrl, successRedirectTo).href,
          payment_method_data: {
            billing_details: {
              address: {
                country: country?.toUpperCase() || 'US',
              },
            },
          },
        },
      })

      if (error) {
        // This point is only reached if there's an immediate error when confirming the Intent.
        // Show the error to your customer (for example, "payment details incomplete").
        console.log('error2', error)
        redirectToResultPage(paymentResultUrl, type, clientSecret)
        return
      } else {
        // Your customer is redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer is redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
      }
    } catch (e) {
      redirectToResultPage(paymentResultUrl, type, clientSecret)
    }
  }

  function onStripeFormReady() {
    setIsLoading(false)
  }

  return (
    <div className="stripe-checkout-form-container">
      {showPaymentMethodsIcons && <PaymentMethodIcons order={paymentMethodsOrder} />}
      <form onSubmit={onSubmit}>
        <PaymentElement options={options} onReady={onStripeFormReady} />

        <div className="button-container">
          <PrimaryButtonWithIcon
            isLoading={isLoading}
            IconComponent={formConfig?.PaymentButtonIconComponent}
            type="submit"
            style={{ textTransform: 'none' }}
            text={formConfig?.paymentButtonText || 'Continue purchase'}
          />
        </div>
      </form>
    </div>
  )
}
